// Single API client. Every backend call in the app goes through here.
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'portal_token';

const client = axios.create({ baseURL: BASE });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Owner-only demo persona: when the platform admin has flipped the walkthrough
  // switch we tell the backend which demo org to act as. Backend ignores it for
  // non-platform-admin tokens, so this header is safe to send unconditionally.
  try {
    const demoOrg = localStorage.getItem('demo_persona_org_id');
    if (demoOrg) config.headers['X-Demo-Org-Id'] = demoOrg;
  } catch { /* ignore */ }
  return config;
});

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = {
  // auth (login/register/demo are public, but the interceptor is harmless there)
  register: (body) => client.post('/auth/register', body),
  login: (body) => client.post('/auth/login', body),
  redeemDemo: (token) => client.post('/auth/demo', { token }),
  inviteInfo: (token) => client.get(`/auth/invite/${token}`),
  acceptInvite: (token, password) => client.post('/auth/accept-invite', { token, password }),
  me: () => client.get('/auth/me'),

  // impersonation ("View as this center")
  adminImpersonateStart: (orgId, userId = null) =>
    client.post(`/admin/orgs/${orgId}/impersonate`, userId ? { user_id: userId } : {}),
  adminImpersonateStop: () => client.post('/admin/impersonate/stop'),
  adminImpersonateEvents: (limit = 50) => client.get('/admin/impersonate/events', { params: { limit } }),

  // org staff (instructors + org admin): shared student roster
  orgContext: () => client.get('/org/context'),
  orgStudents: (includeRemoved = false) => client.get('/org/students', { params: { include_removed: includeRemoved } }),
  orgStudent: (userId) => client.get(`/org/students/${userId}`),
  orgAddStudent: (body) => client.post('/org/students', body),
  orgDeactivateStudent: (userId) => client.put(`/org/students/${userId}/deactivate`),
  orgReactivateStudent: (userId) => client.put(`/org/students/${userId}/reactivate`),
  orgAssign: (userId, body) => client.post(`/org/students/${userId}/assign`, body),
  orgPracticeCatalog: () => client.get('/org/practice-catalog'),
  orgStudentLibrary: (userId) => client.get(`/org/students/${userId}/library`),
  orgStudentSubmissions: (userId) => client.get(`/org/students/${userId}/submissions`),
  orgStudentMockSittings: (userId) => client.get(`/org/students/${userId}/mock-sittings`),
  orgStudentAudioBlob: async (userId, submissionId) => {
    const r = await client.get(`/org/students/${userId}/audio/${submissionId}`, { responseType: 'blob' });
    return URL.createObjectURL(r.data);
  },
  orgCreateAssignment: (userId, body) => client.post(`/org/students/${userId}/assignments`, body),
  orgStudentAssignments: (userId) => client.get(`/org/students/${userId}/assignments`),
  orgDeleteAssignment: (id) => client.delete(`/org/assignments/${id}`),
  // org admin (center admin): instructors + dashboard
  orgInstructors: (includeRemoved = false) => client.get('/org/instructors', { params: { include_removed: includeRemoved } }),
  orgAddInstructor: (body) => client.post('/org/instructors', body),
  orgRemoveInstructor: (userId) => client.delete(`/org/instructors/${userId}`),
  orgInstructorReset: (userId) => client.post(`/org/instructors/${userId}/reset-link`),
  orgInstructorSetPw: (userId, password) => client.post(`/org/instructors/${userId}/set-password`, { password }),
  orgResetLink: (userId) => client.post(`/org/students/${userId}/reset-link`),
  orgStudentSetPw: (userId, password) => client.post(`/org/students/${userId}/set-password`, { password }),
  orgStudentAccess: (userId, body) => client.put(`/org/students/${userId}/access`, body),
  orgCenter: () => client.get('/org/center'),
  orgSetBranding: (body) => client.put('/org/branding', body),
  orgDashboard: () => client.get('/org/dashboard'),
  orgSummary: () => client.get('/org/summary'),
  orgReadiness: () => client.get('/org/readiness'),
  orgReadinessSend: () => client.post('/org/readiness/send'),
  // co-admins (max 2 per center — same powers as org_admin)
  orgAddCoAdmin: (body) => client.post('/org/co-admins', body),
  orgRemoveCoAdmin: (userId) => client.delete(`/org/co-admins/${userId}`),
  orgCoAdminReset: (userId) => client.post(`/org/co-admins/${userId}/reset-link`),
  assignment: (exam) => client.get(`/coach/${exam}/assignment`),
  myAssignments: () => client.get('/coach/assignments'),
  myAssignment: (id) => client.get(`/coach/assignments/${id}`),

  // content + practice
  curriculum: (exam, lang) => client.get(`/content/${exam}/curriculum`, { params: lang && lang !== 'en' ? { lang } : {} }),
  unit: (exam, unitId, lang) => client.get(`/content/${exam}/units/${unitId}`, { params: lang && lang !== 'en' ? { lang } : {} }),
  practiceSetList: (exam, taskType) => client.get(`/content/${exam}/practice/${taskType}/sets`),
  practiceSets: (exam, taskType, count = 1, setId = null) =>
    client.get(`/content/${exam}/practice/${taskType}`, { params: { count, ...(setId ? { set_id: setId } : {}) } }),
  submitPractice: (exam, body) => client.post(`/content/${exam}/practice/submit`, body),
  progress: (exam) => client.get(`/content/${exam}/progress`),
  submitWriting: (exam, body) => client.post(`/submissions/${exam}/writing`, body),
  rescoreSubmission: (exam, id) => client.post(`/submissions/${exam}/${id}/score`),
  submitSpeaking: (exam, formData) => client.post(`/speaking/${exam}/submit`, formData),
  contentAudioBlob: (filename) => client.get(`/media/audio/${filename}`, { responseType: 'blob' }),
  contentImageBlob: (filename) => client.get(`/media/image/${filename}`, { responseType: 'blob' }),
  // Signed URLs (60s) for native <audio>/<img> tags that can't attach a Bearer.
  // Returns { url, expires_in }. url is a same-origin /api/... path — prefix
  // with REACT_APP_BACKEND_URL at the call site to build the absolute src.
  mediaSign: (key, ttl) => client.get('/media/sign', { params: { key, ...(ttl ? { ttl } : {}) } }),
  speakingAudioSign: (exam, submissionId) => client.get(`/speaking/${exam}/audio/sign/${submissionId}`),
  mockIndex: (exam) => client.get(`/content/${exam}/mocks`),
  coverage: (exam) => client.get(`/content/${exam}/coverage`),
  nextAction: (exam) => client.get(`/coach/${exam}/next`),
  setTarget: (exam, targetClb) => client.post(`/coach/${exam}/target`, { target_clb: targetClb }),
  repeatedTraps: (exam) => client.get(`/coach/${exam}/traps`),
  mockForm: (exam, form) => client.get(`/content/${exam}/mock/${form}`),
  mockSectionSubmit: (exam, body) => client.post(`/content/${exam}/mock/section-submit`, body),
  myAttempts: (exam) => client.get(`/content/${exam}/attempts`),
  mockSittings: (exam) => client.get(`/content/${exam}/mock-sittings`),
  mockReview: (exam, form, at) => client.get(`/content/${exam}/mock-review`, { params: { form, at } }),
  practiceReview: (exam, attemptId) => client.get(`/content/${exam}/practice-review/${attemptId}`),
  mySubmissions: (exam) => client.get(`/submissions/${exam}/mine`),
  submissionDetail: (exam, id) => client.get(`/submissions/${exam}/${id}`),
  exemplars: (exam, taskType) => client.get(`/content/${exam}/exemplars/${taskType}`),
  walkthroughs: (exam, lang) => client.get(`/content/${exam}/walkthroughs`, { params: lang && lang !== 'en' ? { lang } : {} }),
  walkthrough: (exam, id, lang) => client.get(`/content/${exam}/walkthrough/${id}`, { params: lang && lang !== 'en' ? { lang } : {} }),

  // platform admin
  adminOrgs: () => client.get('/admin/orgs'),
  adminCreateOrg: (body) => client.post('/admin/orgs', body),
  adminCreateCenter: (body) => client.post('/admin/centers', body),
  adminCreateDemoOrg: (body) => client.post('/admin/demo-orgs', body),
  adminCreateDemoOrgV2: (body) => client.post('/admin/demo-orgs-v2', body),
  adminConvertPaid: (orgId) => client.post(`/admin/orgs/${orgId}/convert-paid`),
  adminInviteOrgAdmin: (orgId, body) => client.post(`/admin/orgs/${orgId}/admins`, body),
  adminUpdateOrg: (orgId, body) => client.patch(`/admin/orgs/${orgId}`, body),
  adminDeleteOrg: (orgId) => client.delete(`/admin/orgs/${orgId}`),
  adminDemoLinks: () => client.get('/admin/demo-links'),
  adminCreateDemoLink: (body) => client.post('/admin/demo-links', body),
  adminContentInventory: () => client.get('/admin/content-inventory'),
  adminSubmissions: (params) => client.get('/admin/submissions', { params }),
  adminAnalyticsOverview: () => client.get('/admin/analytics/overview'),
  adminAnalyticsOrg: (orgId) => client.get(`/admin/analytics/org/${orgId}`),
  adminSpend: () => client.get('/admin/analytics/spend'),
  adminSpendOrg: (orgId) => client.get(`/admin/analytics/spend/org/${orgId}`),
  // ─── new: extended platform-admin control (KPIs, drill-down, block, extend/revoke) ───
  adminKpis: () => client.get('/admin/kpis'),
  adminOrgDetail: (orgId) => client.get(`/admin/orgs/${orgId}/detail`),
  adminSetLimits: (orgId, body) => client.patch(`/admin/orgs/${orgId}/limits`, body),
  adminSetFeatures: (orgId, features) => client.patch(`/admin/orgs/${orgId}/features`, { features }),
  adminExtendAccess: (orgId, days) => client.patch(`/admin/orgs/${orgId}/extend`, { days }),
  adminRevokeAccess: (orgId) => client.post(`/admin/orgs/${orgId}/revoke`),
  adminRestoreAccess: (orgId) => client.post(`/admin/orgs/${orgId}/restore`),
  adminBlockUser: (userId, reason) => client.post(`/admin/users/${userId}/block`, { reason }),
  adminUnblockUser: (userId) => client.post(`/admin/users/${userId}/unblock`),
  adminOrgUsers: (orgId, role) => client.get('/admin/users', { params: { org_id: orgId, ...(role ? { role } : {}) } }),
  adminDeviceAlerts: () => client.get('/admin/devices/alerts'),
  adminForgetDevice: (userAgent) => client.post('/admin/devices/forget', { user_agent: userAgent }),
  adminSignIns: (params) => client.get('/admin/signins', { params }),
  adminDemoAccounts: () => client.get('/admin/demo-accounts'),
};
